import { twMerge } from "tailwind-merge";
import { Button, H1, Icon } from "@sellar-io/components";
import { getServiceWorker } from "../../utils/browserCapabilities";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../modal/Dialog";

export type WalkthroughClient =
  | "CHROME"
  | "SAFARI"
  | "FIREFOX"
  | "ANDROID"
  | "FALLBACK"
  | "IOS";

export const PushNotificationEnableWalkthroughDialog = ({
  onDismiss,
  active,
  client,
}: {
  onDismiss: () => void;
  active: boolean;
  client: WalkthroughClient;
}) => {
  if (!getServiceWorker()) {
    return (
      <Dialog active={active} onDismiss={onDismiss}>
        <DialogNavigation
          title="Open Sellar in your usual browser"
          description="You can keep using Sellar here, but push notifications aren’t available in this browser."
        />
        <DialogContent>
          Use this app’s menu or share button to open the link in Safari or
          Chrome. You can also copy the link and paste it into your usual
          browser.
        </DialogContent>
        <DialogCTA>
          <Button onClick={onDismiss}>Keep using Sellar here</Button>
        </DialogCTA>
      </Dialog>
    );
  }
  const isPWA = window.matchMedia("(display-mode: standalone)").matches;
  const title =
    !isPWA && client == "IOS"
      ? "Add Sellar to home screen"
      : "You have blocked push notifications";
  const description =
    !isPWA && client == "IOS"
      ? "In order to receive push notifications on this device, you need to add Sellar to your home screen."
      : "Follow the steps below to grant Sellar permission to show you push notifications";
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={client !== "FALLBACK" ? "CHEVRON_DOWN" : undefined}
        onClickLeftIcon={onDismiss}
        title={client !== "FALLBACK" ? title : undefined}
        description={client !== "FALLBACK" ? description : undefined}
      />
      <DialogContent>
        <>
          {client === "CHROME" && <EnableNotificationsChrome />}
          {client === "SAFARI" && <EnableNotificationsSafari />}
          {client === "FIREFOX" && <EnableNotificationsFirefox />}
          {client === "ANDROID" && <EnableNotificationsAndroid />}
          {client === "IOS" && <EnableNotificationsIos isPWA={isPWA} />}
          {client === "FALLBACK" && <EnableNotificationsFallback />}
        </>
      </DialogContent>
      {client === "FALLBACK" && (
        <DialogCTA>
          <Button onClick={onDismiss}>Okay</Button>
        </DialogCTA>
      )}
    </Dialog>
  );
};

const stepsClasses = "w-full rounded-lg bg-zinc-100 p-3 items-center";

const EnableNotificationsChrome = () => {
  return (
    <>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Click on the
        <Icon icon="PAGE_INFO" size={0.6} className="mx-1 inline" /> icon in the
        address bar
      </div>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Toggle on <strong>Notifications</strong>{" "}
        <Icon icon="BELL_OFF_OUTLINE" size={0.6} className="mx-1 inline" />
      </div>
    </>
  );
};

const EnableNotificationsSafari = () => {
  return (
    <>
      <div className={twMerge(stepsClasses, "mb-4")}>
        From the Safari tab click <strong>Settings</strong> (
        <Icon icon="APPLE_COMMAND" size={0.6} className="inline" />
        ,)
      </div>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Click on the <strong>Websites</strong>{" "}
        <Icon icon="WEB" size={0.6} className="mx-1 inline" /> tab
      </div>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Scroll to 'Notifications' section
      </div>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Find and select app.sellar.io
      </div>
      <div className={twMerge(stepsClasses, "mb-0")}>
        Select <strong>Allow</strong> from the dropdown menu
      </div>
    </>
  );
};

const EnableNotificationsFirefox = () => {
  return (
    <>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Click on the <strong>Lock</strong>
        <Icon icon="LOCK" size={0.6} className="mx-1 inline" /> icon in the
        address bar
      </div>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Click the arrow in the <strong>Site information</strong> dropdown panel
      </div>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Click <strong>More information</strong>
      </div>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Click the <strong>Permissions</strong> tab
      </div>
      <div className={twMerge(stepsClasses, "mb-0")}>
        Select <strong>Allow</strong> under 'Send Notifications'
      </div>
    </>
  );
};

const EnableNotificationsAndroid = () => {
  return (
    <>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Tap the <strong>Page info</strong>{" "}
        <Icon icon="PAGE_INFO" size={0.6} className="mx-1 inline" /> icon
      </div>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Go to <strong>Permissions</strong>
      </div>
      <div className={twMerge(stepsClasses, "mb-0")}>
        <strong>Permissions</strong> notifications from Sellar
      </div>
    </>
  );
};

const EnableNotificationsIos = ({ isPWA }: { isPWA: boolean }) => {
  return isPWA ? (
    <>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Go to phone <strong>Settings {">"} Notifications</strong>
      </div>
      <div className={twMerge(stepsClasses, "mb-4")}>Find app.sellar.io</div>
      <div className={twMerge(stepsClasses, "mb-4")}>Enable notifications</div>
    </>
  ) : (
    <>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Tap the <strong>Share</strong>
        <Icon icon="EXPORT_VARIANT" size={0.6} className="mx-1 inline" /> button
      </div>
      <div className={twMerge(stepsClasses, "mb-4")}>
        Tap <strong>Add to Home Screen</strong>
        <Icon icon="PLUS_BOX_OUTLINE" size={0.6} className="mx-1 inline" />{" "}
        button
      </div>
    </>
  );
};

const EnableNotificationsFallback = () => {
  return (
    <div className="text-center">
      <H1 className="mb-4">You have blocked push notifications</H1>
      <p className="text-lg">
        Go to your browser settings to allow push notifications from
        app.sellar.io
      </p>
    </div>
  );
};
